import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { fetchCampaignAttribution, fetchCampaigns } from '@/data/sources-marketinga';
import { formatCurrency } from '@/lib/utils';
import type { CampaignAttribution } from '@/types/marketinga';

/* /marketing/campaign-attribution — Campaign Attribution (W3b). Which campaigns
 * produce REAL revenue, joining campaign sends → opportunities.
 *
 * HONESTY + SAFETY INVARIANTS (W3b contract — mirrors W2b RevenueAttribution):
 *  - FREE + read-only: joins stored events and opportunities, NEVER a send,
 *    never a charge. Safe to AUTO-LOAD once a campaign is chosen.
 *  - HONEST NULLS ARE THE POINT: won_value / revenue_per_recipient stay null
 *    until an opportunity is actually Won — rendered as an explicit
 *    "No won opportunity yet", NEVER as £0 or an invented number. A campaign
 *    with real engagement but no win reads as unproven, not unprofitable.
 *  - Source-attributed ("bksesh_sales_lead_events+bc_crm_opportunities"); the
 *    contract's own honesty note is surfaced verbatim.
 */

const SOURCE_TAG = 'Source: Sends → opportunities (bksesh_sales_lead_events + bc_crm_opportunities)';

const FUNNEL_STEPS: Array<{ key: keyof Pick<CampaignAttribution, 'sent' | 'opened' | 'clicked' | 'replied' | 'opportunities'>; label: string }> = [
  { key: 'sent', label: 'Sent' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'replied', label: 'Replied' },
  { key: 'opportunities', label: 'Opportunities' },
];

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** Conversion of a later step against `sent` — honest "—" when there is no base. */
function rateOf(count: number, base: number): string {
  if (base <= 0) return '—';
  return `${((count / base) * 100).toFixed(1)}%`;
}

function FunnelBar({ data }: { data: CampaignAttribution }) {
  const base = data.sent;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      {FUNNEL_STEPS.map((step, i) => {
        const count = data[step.key];
        return (
          <div key={step.key} className="rounded-lg border border-border-subtle bg-surface p-4">
            <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{step.label}</div>
            <div className="mt-1 font-mono text-2xl font-semibold text-text-primary" data-tabular>
              {formatInt(count)}
            </div>
            <div className="mt-0.5 text-[11px] text-text-tertiary">
              {i === 0 ? 'recipients' : `${rateOf(count, base)} of sent`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Won-revenue tile — honest null (no win yet) is explicit, never £0. */
function WonRevenueTile({ data }: { data: CampaignAttribution }) {
  const hasWin = data.won_value !== null;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="rounded-lg border border-border-subtle bg-surface p-4">
        <div className="text-[11px] uppercase tracking-wide text-text-tertiary">Won revenue</div>
        {hasWin ? (
          <div className="mt-1 font-mono text-2xl font-semibold text-text-primary" data-tabular>
            {formatCurrency(data.won_value as number, data.currency)}
          </div>
        ) : (
          <div className="mt-1 text-sm italic text-text-tertiary">No won opportunity yet</div>
        )}
      </div>
      <div className="rounded-lg border border-border-subtle bg-surface p-4">
        <div className="text-[11px] uppercase tracking-wide text-text-tertiary">Revenue / recipient</div>
        {data.revenue_per_recipient !== null ? (
          <div className="mt-1 font-mono text-2xl font-semibold text-text-primary" data-tabular>
            {formatCurrency(data.revenue_per_recipient, data.currency)}
          </div>
        ) : (
          <div className="mt-1 text-2xl text-text-tertiary">—</div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function AttributionBody({ campaignId }: { campaignId: string }) {
  // FREE read-only surface — auto-loads for the selected campaign. No send/charge.
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['marketing', 'campaign-attribution', campaignId],
    queryFn: () => fetchCampaignAttribution(campaignId),
  });

  if (isPending) return <LoadingState rows={5} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const hasActivity = data.sent > 0 || data.opportunities > 0;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="neutral">{SOURCE_TAG}</Badge>
        <span className="text-xs text-text-tertiary">
          {data.won_value !== null
            ? 'Won revenue attributed'
            : 'Engagement tracked · revenue null until a Won opportunity'}
        </span>
      </div>

      <div className="mt-4 space-y-4">
        {!hasActivity ? (
          <EmptyState
            title="No attributed activity yet"
            teach="This campaign hasn't produced tracked sends or opportunities yet. Metrics appear here as sends, opens, clicks and replies are recorded; won revenue is attributed only when an opportunity is actually Won — until then nothing is claimed."
          />
        ) : (
          <>
            <FunnelBar data={data} />
            <WonRevenueTile data={data} />
          </>
        )}
        <div className="rounded-md border border-border-subtle bg-inset/40 px-3 py-2 text-[11px] text-text-tertiary">
          Read-only from {data.source} · {data.note}
        </div>
      </div>
    </Card>
  );
}

/* ========================================================================== */
export function CampaignAttributionPage() {
  // Free campaign list feeds the selector; attribution loads per selection.
  const campaignsQ = useQuery({ queryKey: ['marketing', 'campaigns'], queryFn: fetchCampaigns });
  const campaigns = campaignsQ.data?.campaigns ?? [];

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const campaignId =
    selectedId !== null && campaigns.some((c) => c.id === selectedId)
      ? selectedId
      : (campaigns[0]?.id ?? null);

  const selector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Campaign</span>
      <select
        value={campaignId ?? ''}
        onChange={(e) => setSelectedId(e.target.value)}
        className="h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
        aria-label="Campaign"
        disabled={campaigns.length === 0}
      >
        {campaigns.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Campaign Attribution"
        subtitle="Which campaigns produce real revenue — sends joined to won opportunities. Revenue stays honest: null until an opportunity is actually Won."
        actions={campaigns.length > 0 ? selector : undefined}
      />

      {campaignsQ.isPending ? (
        <LoadingState rows={5} />
      ) : campaignsQ.error ? (
        <ErrorState error={campaignsQ.error} onRetry={() => void campaignsQ.refetch()} />
      ) : campaigns.length === 0 || campaignId === null ? (
        <EmptyState
          title="No campaigns to attribute yet"
          teach="Create a campaign on the Campaigns page first. Once a campaign exists and its sends and opportunities are recorded, its honest attribution — engagement now, won revenue only when an opportunity is Won — appears here."
        />
      ) : (
        <AttributionBody key={campaignId} campaignId={campaignId} />
      )}
    </div>
  );
}
